import { apiClient, PaginatedResponse } from './client';
import axios from 'axios';
import {
  ServiceApplication,
  ServiceApplicationStatistics,
  ServiceApplicationFilters,
  ApproveApplicationData,
  RejectApplicationData,
  AttachServiceData,
  RequestInfoData,
  ScheduleSiteVisitData,
} from '../types/serviceApplication';

export const serviceApplicationsApi = {
  // Get all applications with filters
  getApplications: async (filters: ServiceApplicationFilters & { page?: number; per_page?: number }) => {
    const response = await apiClient.get<PaginatedResponse<ServiceApplication>>('/admin/county/service-applications', {
      params: filters,
    });
    return response.data;
  },

  // Get statistics
  getStatistics: async () => {
    const response = await apiClient.get<{ status: string; data: ServiceApplicationStatistics }>(
      '/admin/county/service-applications/statistics'
    );
    // Handle both nested and direct response structures
    const data = response.data?.data || response.data;
    // If data has a 'data' property, extract it
    if (data && typeof data === 'object' && 'data' in data) {
      return (data as any).data as ServiceApplicationStatistics;
    }
    return data as ServiceApplicationStatistics;
  },

  // Get single application
  getApplication: async (id: number) => {
    const response = await apiClient.get<{ status: string; data: ServiceApplication }>(
      `/admin/county/service-applications/${id}`
    );
    // Handle both nested and direct response structures
    const data = response.data?.data || response.data;
    // If data has a 'data' property, extract it
    if (data && typeof data === 'object' && 'data' in data) {
      return (data as any).data as ServiceApplication;
    }
    return data as ServiceApplication;
  },

  // Get review data (services, zones, etc.)
  getReviewData: async (id: number) => {
    const response: any = await apiClient.get<any>(`/admin/county/service-applications/${id}/review`);
    // apiClient.get() already unwraps axios response.data, so:
    // response = { status: "success", data: { services, zones, application, charge } }
    // Unwrap nested data until we find the object with 'services'
    let result = response;
    while (result && typeof result === 'object' && !Array.isArray(result) && !('services' in result) && 'data' in result) {
      result = result.data;
    }
    return result;
  },

  // Start review
  startReview: async (id: number) => {
    const response = await apiClient.post(`/admin/county/service-applications/${id}/start-review`);
    return response.data?.data;
  },

  // Approve application
  approve: async (id: number, data: ApproveApplicationData) => {
    const response = await apiClient.post(`/admin/county/service-applications/${id}/approve`, data);
    return response.data;
  },

  // Reject application
  reject: async (id: number, data: RejectApplicationData) => {
    const response = await apiClient.post(`/admin/county/service-applications/${id}/reject`, data);
    return response.data;
  },

  // Attach service
  attachService: async (id: number, data: AttachServiceData) => {
    const response = await apiClient.post(`/admin/county/service-applications/${id}/attach-service`, data);
    return response.data;
  },

  // Request additional info
  requestInfo: async (id: number, data: RequestInfoData) => {
    const response = await apiClient.post(`/admin/county/service-applications/${id}/request-info`, data);
    return response.data;
  },

  // Schedule site visit
  scheduleSiteVisit: async (id: number, data: ScheduleSiteVisitData) => {
    const response = await apiClient.post(`/admin/county/service-applications/${id}/schedule-site-visit`, data);
    return response.data;
  },

  // Export applications
  exportApplications: async (filters: ServiceApplicationFilters) => {
    // Use axios directly for blob responses to avoid JSON parsing
    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

    const response = await axios.get(`${baseURL}/admin/county/service-applications/export`, {
      params: filters,
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/csv',
      },
    });

    return response.data;
  },
};
